import { useMemo } from "react"
import { motion, AnimatePresence } from "motion/react"
import { useRenderContext, RenderContext } from "../core/render-context"
import type { CogoElement } from "../core/types"
import { useAnimation } from "./useAnimation"
import { useScrollProgress } from "./useScrollProgress"
import { resolveEffectsClasses, resolveEffectsStyle } from "../runtime/effects-resolver"
import { BackgroundLayers } from "../components/background-layers"
import { positionToken } from "../lib/design-tokens"
import { evaluateTernary } from "@cogo/runtime"

export function ElementRenderer({ element: rawElement }: { element: CogoElement }) {
  const ctx = useRenderContext()
  const { registry, resolveSymbol } = ctx

  // Guard: skip rendering if element is null/undefined (malformed tree)
  if (!rawElement) return null

  // Normalize element: flatten options/styling/spacing onto element for renderers
  // that expect flat properties (e.g., element.text instead of element.options.text)
  let element = normalizeElement(rawElement)

  // Resolve dynamic #{...} expressions in className (e.g., ternary for active states)
  if (element.className?.includes("#{")) {
    const resolved = resolveSymbol(element.className)
    if (typeof resolved === "string" && resolved !== element.className) {
      element = { ...element, className: resolved }
    }
  }

  // Resolve visibility
  if (element.visible !== undefined) {
    const visible = resolveSymbol(String(element.visible))
    if (visible === false || visible === "false" || visible == null) return null
  }

  // Handle element-level iteration (non-block-ref elements)
  if (element.iterateOver && element.type !== "block-ref") {
    const items = resolveSymbol(element.iterateOver)
    if (!Array.isArray(items)) return null

    // Container with iterateOver + children:
    // - If the container IS a grid layout wrapper (layout.type=grid or className has "grid"),
    //   render the container ONCE and iterate children inside → IteratedContainer
    // - Otherwise (card/template with flex/column layout), repeat the entire container
    //   N times as separate siblings → fall through to IteratedElement below
    const isGridWrapper =
      element.layout?.type === "grid" || /(?:^|\s)grid(?:\s|$)/.test(element.className ?? "")
    if (element.type === "container" && element.children?.length && isGridWrapper) {
      const containerElement: CogoElement = { ...element, iterateOver: undefined, itemVar: undefined }
      // Replace children with flattened iterated children
      containerElement.children = items.flatMap((item, i) => {
        const itemKey = (item != null && typeof item === "object" && "id" in item)
          ? String((item as Record<string, unknown>).id)
          : String(i)
        return (element.children ?? []).map((child) => ({
          ...child,
          // Make each child's ID unique per iteration
          id: `${child.id}__iter_${itemKey}`,
          // Pass iteration context via a wrapper marker
          iterateOver: undefined,
          _iterItem: item,
          _iterVar: element.itemVar,
        }))
      }) as CogoElement[]
      // Render container with the flattened children using IteratedChildren wrapper
      return <IteratedContainer element={containerElement} items={items} originalChildren={element.children} itemVar={element.itemVar} parentCtx={ctx} />
    }

    return (
      <>
        {items.map((item, i) => {
          const itemKey = (item != null && typeof item === "object" && "id" in item)
            ? String((item as Record<string, unknown>).id)
            : String(i)
          return (
            <IteratedElement
              key={itemKey}
              element={element}
              item={item}
              itemVar={element.itemVar}
              parentCtx={ctx}
              childIndex={i}
            />
          )
        })}
      </>
    )
  }

  // Resolve #{...} expressions in className for ALL elements (not just iterated)
  // This enables ternary expressions like #{state.x ? 'bg-red' : 'bg-gray'} to work
  // when the element is a descendant of an iterated container
  if (element.className?.includes("#{")) {
    const resolved = resolveSymbol(element.className)
    if (typeof resolved === "string" && resolved !== element.className) {
      element = { ...element, className: resolved }
    }
  }

  const Renderer = registry.get(element.type)
  if (!Renderer) {
    console.warn(`No renderer for type: ${element.type}`)
    return null
  }

  // In edit mode, add data-element-id for editor click detection
  const isEditMode = ctx.mode === "edit"
  const editAttrs: Record<string, string> | undefined = isEditMode
    ? { "data-element-id": element.id }
    : undefined

  // Wrap with absolute positioning if element has position
  // Skip wrapping for badge type — BadgeRenderer handles position internally
  if (element.position?.type === "absolute" && element.type !== "badge") {
    const posClasses = [
      "absolute",
      element.position.top ? `top-${positionToken(element.position.top)}` : "",
      element.position.right ? `right-${positionToken(element.position.right)}` : "",
      element.position.bottom ? `bottom-${positionToken(element.position.bottom)}` : "",
      element.position.left ? `left-${positionToken(element.position.left)}` : "",
    ].filter(Boolean).join(" ")

    return (
      <div className={posClasses} {...(editAttrs ?? {})}>
        <AnimatedElement element={element}>
          <Renderer element={element} />
        </AnimatedElement>
      </div>
    )
  }

  // When no wrapper is needed, add data-element-id directly if in edit mode
  if (isEditMode) {
    return (
      <AnimatedElement element={element} editAttrs={editAttrs}>
        <Renderer element={element} />
      </AnimatedElement>
    )
  }

  return (
    <AnimatedElement element={element}>
      <Renderer element={element} />
    </AnimatedElement>
  )
}

function AnimatedElement({
  element,
  childIndex = 0,
  children,
  editAttrs,
}: {
  element: CogoElement
  childIndex?: number
  children: React.ReactNode
  editAttrs?: Record<string, string>
}) {
  const { ref, initial, animate, exit, transition } = useAnimation(
    { id: element.id, animation: element.animation },
    childIndex
  )
  const { ref: scrollRef, style: scrollStyle, isScrollProgress } = useScrollProgress(
    element.animation ?? undefined,
  )

  const hasEffects = element.effects != null
  const hasBgLayers =
    element.backgroundLayers != null && element.backgroundLayers.length > 0
  const hasAnimation = element.animation != null
  const hasScrollAnim = isScrollProgress
  const hasTransform = element.transform != null
  const hasTransition = element.transition != null

  // No wrapper needed if no animation, effects, bg layers, transform, or transition
  if (!hasAnimation && !hasScrollAnim && !hasEffects && !hasBgLayers && !hasTransform && !hasTransition) {
    // In edit mode, add data attribute via a thin wrapper that doesn't affect layout
    if (editAttrs && Object.keys(editAttrs).length > 0) {
      return <div {...editAttrs} className="contents">{children}</div>
    }
    return <>{children}</>
  }

  const effectsClasses = hasEffects
    ? resolveEffectsClasses(element.effects)
    : ""
  const effectsStyle = hasEffects ? resolveEffectsStyle(element.effects) : {}

  // Resolve element.transform → inline CSS transform on wrapper
  const transformStyle: React.CSSProperties = {}
  if (element.transform) {
    const t = element.transform
    const parts: string[] = []
    if (t.scale != null) parts.push(`scale(${t.scale})`)
    else if (t.scaleX != null || t.scaleY != null) parts.push(`scale(${t.scaleX ?? 1}, ${t.scaleY ?? 1})`)
    if (t.rotate != null) parts.push(`rotate(${t.rotate}deg)`)
    if (t.translateX != null) parts.push(`translateX(${t.translateX}px)`)
    if (t.translateY != null) parts.push(`translateY(${t.translateY}px)`)
    if (t.skewX != null) parts.push(`skewX(${t.skewX}deg)`)
    if (t.skewY != null) parts.push(`skewY(${t.skewY}deg)`)
    if (parts.length) transformStyle.transform = parts.join(" ")
    if (t.origin) transformStyle.transformOrigin = t.origin
  }

  // Resolve element.transition → inline CSS transition on wrapper
  if (element.transition) {
    const tr = element.transition
    const props = tr.properties?.join(", ") ?? "all"
    const dur = `${tr.duration ?? 150}ms`
    const ease = tr.curve ?? "ease"
    transformStyle.transition = `${props} ${dur} ${ease}${tr.delay ? ` ${tr.delay}ms` : ""}`
  }

  // Resolve effects clipPath/maskImage on wrapper
  if (element.effects?.clipPath) {
    transformStyle.clipPath = element.effects.clipPath
  }
  if (element.effects?.maskImage) {
    transformStyle.maskImage = element.effects.maskImage
    ;(transformStyle as Record<string, unknown>).WebkitMaskImage = element.effects.maskImage
  }

  const mergedStyle = { ...effectsStyle, ...transformStyle }
  const hasMergedStyle = Object.keys(mergedStyle).length > 0
  const needsRelative = hasBgLayers

  // When element has sticky/fixed positioning, apply position classes to the
  // wrapper div so that CSS positioning is on the outermost element (otherwise
  // the extra wrapper breaks sticky/fixed behaviour).
  const posType = element.position?.type
  const needsPositionOnWrapper = posType === "sticky" || posType === "fixed"
  const positionWrapperClasses: string[] = []
  if (needsPositionOnWrapper) {
    positionWrapperClasses.push(posType!)
    const pos = element.position!
    if (pos.top != null) positionWrapperClasses.push(`top-${pos.top}`)
    if (pos.right != null) positionWrapperClasses.push(`right-${pos.right}`)
    if (pos.bottom != null) positionWrapperClasses.push(`bottom-${pos.bottom}`)
    if (pos.left != null) positionWrapperClasses.push(`left-${pos.left}`)
    // Preserve z-index from className (e.g. "z-50")
    const zMatch = element.className?.match(/z-\d+/)
    if (zMatch) positionWrapperClasses.push(zMatch[0])
  }

  const combinedClassName =
    [effectsClasses, needsRelative ? "relative" : "", ...positionWrapperClasses, "min-w-0"]
      .filter(Boolean)
      .join(" ") || undefined

  // Use motion.div for scroll-progress (continuous scroll-linked transforms)
  if (hasScrollAnim && scrollStyle) {
    return (
      <motion.div
        ref={scrollRef as React.RefObject<HTMLDivElement>}
        className={combinedClassName}
        style={{
          ...(hasMergedStyle ? mergedStyle as React.CSSProperties : {}),
          ...scrollStyle,
        } as React.CSSProperties}
        {...editAttrs}
      >
        {hasBgLayers && (
          <BackgroundLayers layers={element.backgroundLayers!} />
        )}
        {children}
      </motion.div>
    )
  }

  // Use motion.div for animated elements, plain div for effects/bg-only
  if (hasAnimation) {
    return (
      <motion.div
        ref={ref as React.RefObject<HTMLDivElement>}
        className={combinedClassName}
        style={hasMergedStyle ? mergedStyle as React.CSSProperties : undefined}
        initial={initial}
        animate={animate}
        exit={exit}
        transition={transition}
        {...editAttrs}
      >
        {hasBgLayers && (
          <BackgroundLayers layers={element.backgroundLayers!} />
        )}
        {children}
      </motion.div>
    )
  }

  return (
    <div
      className={combinedClassName}
      style={hasMergedStyle ? mergedStyle as React.CSSProperties : undefined}
      {...editAttrs}
    >
      {hasBgLayers && (
        <BackgroundLayers layers={element.backgroundLayers!} />
      )}
      {children}
    </div>
  )
}



/**
 * Renders a container element ONCE with its layout (grid/flex), then iterates
 * children inside it for each item. This preserves the container's grid-cols-N
 * classes while repeating children per data item.
 */
function IteratedContainer({
  element,
  items,
  originalChildren,
  itemVar,
  parentCtx,
}: {
  element: CogoElement
  items: unknown[]
  originalChildren: CogoElement[]
  itemVar?: string
  parentCtx: ReturnType<typeof useRenderContext>
}) {
  const { registry } = parentCtx
  const Renderer = registry.get(element.type)
  if (!Renderer) return null

  // Build a container element whose children are the iterated items
  const containerEl: CogoElement = {
    ...element,
    children: [], // Will be replaced by rendered children below
  }

  return (
    <RenderContext.Provider value={parentCtx}>
      <Renderer element={containerEl}>
        {items.map((item, i) => {
          const itemKey = (item != null && typeof item === "object" && "id" in item)
            ? String((item as Record<string, unknown>).id)
            : String(i)
          return originalChildren.map((child) => (
            <IteratedElement
              key={`${child.id}-${itemKey}`}
              element={child}
              item={item}
              itemVar={itemVar}
              parentCtx={parentCtx}
              childIndex={i}
            />
          ))
        })}
      </Renderer>
    </RenderContext.Provider>
  )
}

function IteratedElement({
  element,
  item,
  itemVar,
  parentCtx,
  childIndex = 0,
}: {
  element: CogoElement
  item: unknown
  itemVar?: string
  parentCtx: ReturnType<typeof useRenderContext>
  childIndex?: number
}) {
  // Combined resolver: resolves item paths locally, delegates others to parent
  const combinedResolve = useMemo(() => {
    return (path: string): unknown => {
      if (itemVar) {
        if (path === itemVar) return item
        if (path.startsWith(`${itemVar}.`)) {
          return getNestedValue(item, path.slice(itemVar.length + 1))
        }
      }
      return parentCtx.resolveSymbol(`#{${path}}`)
    }
  }, [parentCtx, itemVar, item])

  const childCtx = useMemo(() => {
    const parentItemContext = parentCtx.itemContext ?? {}
    return {
      ...parentCtx,
      itemContext: { ...parentItemContext, ...(itemVar ? { [itemVar]: item } : {}) },
      resolveSymbol: (expr: string) => {
        if (itemVar && typeof expr === "string") {
          // Pure #{...} expression (single wrapper, no embedded templates)
          const pureMatch = /^#\{([^}]+)\}$/.exec(expr)
          if (pureMatch) {
            const inner = pureMatch[1].trim()

            // Ternary MUST be checked before item property path, because
            // "recipe.isFavorite ? 'a' : 'b'" starts with "recipe." but is a ternary
            if (inner.includes("?")) {
              const tv = evaluateTernary(inner, combinedResolve)
              if (tv !== undefined) return tv
            }

            // Direct item reference: #{item} when item is a primitive
            if (inner === itemVar) {
              return item
            }

            // Item property: #{item.name}
            if (inner.startsWith(`${itemVar}.`)) {
              const path = inner.slice(itemVar.length + 1)
              return getNestedValue(item, path)
            }
          }

          // Template with multiple #{...} patterns: "#{item.name} x#{item.quantity}"
          if (!pureMatch && expr.includes("#{")) {
            let hasItemRef = false
            const resolved = expr.replace(/#\{([^}]+)\}/g, (_fullMatch, ref) => {
              const r = (ref as string).trim()
              // Ternary MUST be checked before item property path, because
              // "recipe.isFavorite ? 'a' : 'b'" starts with "recipe." but is a ternary
              if (r.includes("?")) {
                const tv = evaluateTernary(r, combinedResolve)
                if (tv !== undefined) {
                  hasItemRef = true
                  return tv != null ? String(tv) : ""
                }
              }
              // Direct item reference: #{item}
              if (r === itemVar) {
                hasItemRef = true
                return item != null ? String(item) : ""
              }
              // Item property path: #{item.name}
              if (r.startsWith(`${itemVar}.`)) {
                hasItemRef = true
                const path = r.slice(itemVar.length + 1)
                const val = getNestedValue(item, path)
                return val != null ? String(val) : ""
              }
              // Non-item refs: delegate to parent context
              const val = parentCtx.resolveSymbol(`#{${ref}}`)
              return val != null ? String(val) : ""
            })
            if (hasItemRef) return resolved
          }
        }

        return parentCtx.resolveSymbol(expr)
      },
    }
  }, [parentCtx, itemVar, item, combinedResolve])

  const { registry } = parentCtx
  const Renderer = registry.get(element.type)
  if (!Renderer) return null

  // Render without iterateOver to prevent infinite loop
  let elementWithoutIteration: CogoElement = { ...element, iterateOver: undefined, itemVar: undefined }

  // Resolve dynamic #{...} expressions in className for iterated elements
  if (elementWithoutIteration.className?.includes("#{")) {
    const resolved = childCtx.resolveSymbol(elementWithoutIteration.className)
    if (typeof resolved === "string" && resolved !== elementWithoutIteration.className) {
      elementWithoutIteration = { ...elementWithoutIteration, className: resolved }
    }
  }

  // In edit mode, add data-element-id for editor click detection
  const isEditMode = parentCtx.mode === "edit"
  const editAttrs: Record<string, string> | undefined = isEditMode
    ? { "data-element-id": element.id }
    : undefined

  return (
    <RenderContext.Provider value={childCtx}>
      <AnimatedElement element={elementWithoutIteration} childIndex={childIndex} editAttrs={editAttrs}>
        <Renderer element={elementWithoutIteration} />
      </AnimatedElement>
    </RenderContext.Provider>
  )
}

function getNestedValue(obj: unknown, path: string): unknown {
  // Split path by "." but also handle bracket notation: "features[0]" → ["features", "0"]
  const parts: string[] = []
  for (const segment of path.split(".")) {
    const bracketMatch = /^(\w+)\[(\d+)\]$/.exec(segment)
    if (bracketMatch) {
      parts.push(bracketMatch[1], bracketMatch[2])
    } else {
      parts.push(segment)
    }
  }
  let current: unknown = obj
  for (const part of parts) {
    if (current == null) return undefined
    if (Array.isArray(current)) {
      const idx = parseInt(part, 10)
      current = isNaN(idx) ? undefined : current[idx]
    } else if (typeof current === "object") {
      current = (current as Record<string, unknown>)[part]
    } else {
      return undefined
    }
  }
  return current
}

/**
 * Normalize element: flatten options/styling/spacing onto element.
 *
 * Blocks from the template factory use nested format:
 *   { "type": "text", "options": { "text": "Hello", "size": 40, "weight": "bold" } }
 *   { "type": "container", "styling": { "borderRadius": 16 }, "spacing": { "padding": { "all": 32 } } }
 *   { "type": "button", "options": { "label": "Click", "variant": "outline", "fullWidth": true } }
 *
 * Renderers expect flat format:
 *   { "type": "text", "text": "Hello", "size": "3xl", "weight": "bold" }
 *   { "type": "button", "text": "Click", "variant": "outline", "fill": true }
 *
 * This function bridges the gap by spreading options properties onto the element,
 * with special mappings for known fields like label→text and fullWidth→fill.
 */
function normalizeElement(el: CogoElement): CogoElement {
  const opts = el.options as Record<string, unknown> | undefined
  const rawLayout = el.layout as Record<string, unknown> | undefined
  const needsLayoutNorm = rawLayout && (rawLayout.alignItems || rawLayout.justifyContent || typeof rawLayout.gap === "number")
  // Fallback: v1.0 blocks use "style" instead of "styling" — normalize to "styling"
  const rawStyle = (el as unknown as Record<string, unknown>).style as Record<string, unknown> | undefined
  const hasFallbackStyle = rawStyle && !el.styling

  // Normalize gridColsResponsive → layout.columns + layout.responsive
  const rawEl = el as unknown as Record<string, unknown>
  const hasGridColsResponsive = rawEl.gridColsResponsive != null

  if (!opts && !el.styling && !el.spacing && !needsLayoutNorm && !hasFallbackStyle && !hasGridColsResponsive) return el

  const normalized = { ...el }

  // Map v1.0 "style" → "styling" for backwards compatibility
  if (hasFallbackStyle && rawStyle) {
    normalized.styling = rawStyle as unknown as CogoElement["styling"]
    delete (normalized as unknown as Record<string, unknown>).style
  }

  // Normalize layout property names: JSON factory format → renderer format
  if (needsLayoutNorm && rawLayout) {
    const normLayout = { ...rawLayout } as Record<string, unknown>
    // alignItems → align
    if (normLayout.alignItems && !normLayout.align) {
      normLayout.align = normLayout.alignItems
      delete normLayout.alignItems
    }
    // justifyContent → justify
    if (normLayout.justifyContent && !normLayout.justify) {
      normLayout.justify = normLayout.justifyContent
      delete normLayout.justifyContent
    }
    // Numeric gap → Tailwind token string
    if (typeof normLayout.gap === "number") {
      normLayout.gap = numericGapToToken(normLayout.gap as number)
    }
    normalized.layout = normLayout as unknown as CogoElement["layout"]
  }

  // Normalize gridColsResponsive → layout.columns + layout.responsive
  // LLM generates: { gridColsResponsive: { sm: 1, md: 2, lg: 3 }, layout: { type: "grid" } }
  // Renderer expects: { layout: { type: "grid", columns: 1, responsive: { md: { columns: 2 }, lg: { columns: 3 } } } }
  if (hasGridColsResponsive) {
    const gcr = rawEl.gridColsResponsive as Record<string, number>
    const layout = (normalized.layout ?? {}) as Record<string, unknown>
    if (!layout.type) layout.type = "grid"
    // Find smallest breakpoint value as mobile-first columns
    const bpOrder = ["sm", "md", "lg", "xl", "2xl"] as const
    const entries = Object.entries(gcr).sort(
      (a, b) => bpOrder.indexOf(a[0] as typeof bpOrder[number]) - bpOrder.indexOf(b[0] as typeof bpOrder[number])
    )
    if (entries.length > 0) {
      layout.columns = entries[0][1] // smallest breakpoint = mobile-first
      const responsive = (layout.responsive ?? {}) as Record<string, Record<string, unknown>>
      for (let i = 1; i < entries.length; i++) {
        const [bp, cols] = entries[i]
        if (!responsive[bp]) responsive[bp] = {}
        responsive[bp].columns = cols
      }
      layout.responsive = responsive
    }
    normalized.layout = layout as unknown as CogoElement["layout"]
    // Clean up the non-standard property
    delete (normalized as unknown as Record<string, unknown>).gridColsResponsive
  }

  if (opts) {
    // Map options.label → text (for button elements)
    if (opts.label && !normalized.text) {
      normalized.text = String(opts.label)
    }
    // Map options.text → text
    if (opts.text && !normalized.text) {
      normalized.text = String(opts.text)
    }
    // Map options.variant → variant
    if (opts.variant && !normalized.variant) {
      normalized.variant = String(opts.variant)
    }
    // Map options.size → size (keep numeric for text-renderer's numericSizeToToken)
    if (opts.size != null && !normalized.size) {
      normalized.size = typeof opts.size === "number" ? String(opts.size) : String(opts.size)
    }
    // Map options.fullWidth → fill
    if (opts.fullWidth && !normalized.fill) {
      normalized.fill = true
    }
    // Map options.urlImage → src (ImageRenderer reads element.src)
    if (opts.urlImage && !normalized.src) {
      normalized.src = String(opts.urlImage)
    }
    // Map options.checked → checked (for switch)
    if (opts.checked !== undefined && normalized.checked === undefined) {
      (normalized as Record<string, unknown>).checked = opts.checked
    }
    // Convert object margin → Tailwind margin classes (rehype pipeline generates { left: "auto", right: "auto" })
    if (opts.margin && typeof opts.margin === "object" && !normalized.margin) {
      const mar = opts.margin as Record<string, unknown>
      const parts: string[] = []
      if (mar.left === "auto" && mar.right === "auto") parts.push("mx-auto")
      else {
        if (mar.left === "auto") parts.push("ml-auto")
        else if (typeof mar.left === "number") parts.push(`ml-${numericPaddingToToken(mar.left)}`)
        if (mar.right === "auto") parts.push("mr-auto")
        else if (typeof mar.right === "number") parts.push(`mr-${numericPaddingToToken(mar.right)}`)
      }
      if (typeof mar.top === "number") parts.push(`mt-${numericPaddingToToken(mar.top)}`)
      if (typeof mar.bottom === "number") parts.push(`mb-${numericPaddingToToken(mar.bottom)}`)
      if (parts.length > 0) normalized.margin = parts.join(" ")
    }
    // Convert object padding → pass to resolvePadding in container-renderer (already handled)
    // Skip: padding is handled by container-renderer.resolvePadding() which now supports objects
    // Generic passthrough: spread remaining options onto element
    // Handles icon, iconPosition, iconClassName, placeholder, src, alt, etc.
    // Skip object-type margin (already converted above) to prevent clsx treating keys as classnames
    for (const [key, value] of Object.entries(opts)) {
      if (value !== undefined && (normalized as Record<string, unknown>)[key] === undefined) {
        // Skip object margin — already converted to Tailwind classes above
        if (key === "margin" && typeof value === "object") continue
        (normalized as Record<string, unknown>)[key] = value
      }
    }
  }

  // Fallback: urlImage at element level → src (for image elements from rehype pipeline)
  const rawNorm = normalized as unknown as Record<string, unknown>
  if (rawNorm.urlImage && !normalized.src) {
    normalized.src = String(rawNorm.urlImage)
  }

  // Normalize spacing.padding → className (for container)
  const spacing = el.spacing as Record<string, unknown> | undefined
  if (spacing?.padding && !normalized.padding) {
    const pad = spacing.padding as Record<string, unknown>
    if (pad.all) {
      normalized.padding = numericPaddingToToken(pad.all as number)
    } else {
      // Directional padding: { top, bottom, left, right, horizontal, vertical }
      const paddingParts: string[] = []
      if (pad.horizontal != null) {
        const token = numericPaddingToToken(pad.horizontal as number)
        paddingParts.push(`px-${token}`)
      }
      if (pad.vertical != null) {
        const token = numericPaddingToToken(pad.vertical as number)
        paddingParts.push(`py-${token}`)
      }
      if (pad.top != null) paddingParts.push(`pt-${numericPaddingToToken(pad.top as number)}`)
      if (pad.bottom != null) paddingParts.push(`pb-${numericPaddingToToken(pad.bottom as number)}`)
      if (pad.left != null) paddingParts.push(`pl-${numericPaddingToToken(pad.left as number)}`)
      if (pad.right != null) paddingParts.push(`pr-${numericPaddingToToken(pad.right as number)}`)
      if (paddingParts.length > 0) {
        normalized.className = [normalized.className, ...paddingParts].filter(Boolean).join(" ")
      }
    }
  }

  // Normalize spacing.margin → className
  if (spacing?.margin) {
    const mar = spacing.margin as Record<string, unknown>
    const marginParts: string[] = []
    if (mar.all != null) marginParts.push(`m-${numericPaddingToToken(mar.all as number)}`)
    if (mar.horizontal != null) marginParts.push(`mx-${numericPaddingToToken(mar.horizontal as number)}`)
    if (mar.vertical != null) marginParts.push(`my-${numericPaddingToToken(mar.vertical as number)}`)
    if (mar.top != null) marginParts.push(`mt-${numericPaddingToToken(mar.top as number)}`)
    if (mar.bottom != null) marginParts.push(`mb-${numericPaddingToToken(mar.bottom as number)}`)
    if (mar.left != null) marginParts.push(`ml-${numericPaddingToToken(mar.left as number)}`)
    if (mar.right != null) marginParts.push(`mr-${numericPaddingToToken(mar.right as number)}`)
    if (marginParts.length > 0) {
      normalized.className = [normalized.className, ...marginParts].filter(Boolean).join(" ")
    }
  }

  // Normalize styling → className additions
  const styling = el.styling as Record<string, unknown> | undefined
  if (styling) {
    const extraClasses: string[] = []
    // border radius from styling.borderRadius OR styling.border.radius
    const borderRadiusValue = (styling.borderRadius as number | undefined)
      ?? ((styling.border as Record<string, unknown> | undefined)?.radius as number | undefined)
    if (borderRadiusValue) {
      const r = borderRadiusValue
      if (r >= 9999) extraClasses.push("rounded-full")
      else if (r >= 16) extraClasses.push("rounded-2xl")
      else if (r >= 12) extraClasses.push("rounded-xl")
      else if (r >= 8) extraClasses.push("rounded-lg")
      else if (r >= 4) extraClasses.push("rounded-md")
      else extraClasses.push("rounded-sm")
    }
    if (styling.shadow) {
      const s = styling.shadow as string
      extraClasses.push(`shadow-${s}`)
    }
    if (styling.border) {
      const border = styling.border as Record<string, unknown>
      if (border.width) {
        extraClasses.push(border.width === 1 ? "border" : `border-${border.width}`)
      }
    }
    // borderTop / borderBottom
    if (styling.borderTop) {
      const bt = styling.borderTop as Record<string, unknown>
      if (bt.width) extraClasses.push("border-t")
    }
    if (styling.borderBottom) {
      const bb = styling.borderBottom as Record<string, unknown>
      if (bb.width) extraClasses.push("border-b")
    }
    if (extraClasses.length > 0) {
      normalized.className = [normalized.className, ...extraClasses].filter(Boolean).join(" ")
    }
    // maxWidth from styling
    if (styling.maxWidth && !normalized.maxWidth) {
      normalized.maxWidth = `${styling.maxWidth}px`
    }
  }

  // Grid span: gridSpan { column, row } → className additions
  // This ensures ALL element types (card, container, etc.) get grid span classes
  const gridSpan = el.gridSpan as Record<string, unknown> | undefined
  if (gridSpan) {
    const spanClasses: string[] = []
    if (gridSpan.column) spanClasses.push(`col-span-${gridSpan.column}`)
    if (gridSpan.row) spanClasses.push(`row-span-${gridSpan.row}`)
    if (spanClasses.length > 0) {
      normalized.className = [normalized.className, ...spanClasses].filter(Boolean).join(" ")
    }
  }

  // Stagger propagation: when parent has animation.stagger > 0, propagate the
  // parent's enter animation to children that don't define their own animation.
  // Children that already have animation get _staggerDelay injected instead.
  normalized.children = propagateStagger(normalized)

  return normalized
}

/**
 * Propagate parent stagger animation to children.
 *
 * When a parent element has `animation.stagger > 0`:
 * - Children WITH their own animation: inject _staggerDelay = i * stagger
 *   into the child's enter.delay, preserving the child's own animation config.
 * - Children WITHOUT animation: inject a copy of the parent's enter animation
 *   with delay = i * stagger, plus the parent's scroll config if present.
 *
 * Returns the (potentially modified) children array, or undefined if no children.
 */
function propagateStagger(parent: CogoElement): CogoElement[] | undefined {
  const children = parent.children
  if (!children || children.length === 0) return children

  const parentAnim = parent.animation
  if (!parentAnim || !parentAnim.stagger || parentAnim.stagger <= 0) return children
  if (!parentAnim.enter) return children

  const stagger = parentAnim.stagger
  return children.map((child, i) => {
    const staggerDelay = i * stagger
    if (staggerDelay === 0 && child.animation) return child // index 0 with own anim: no change needed

    if (child.animation) {
      // Child has its own animation — add stagger delay to existing enter.delay
      const childEnter = child.animation.enter
      if (!childEnter) return child
      const existingDelay = childEnter.delay ?? 0
      return {
        ...child,
        animation: {
          ...child.animation,
          enter: { ...childEnter, delay: existingDelay + staggerDelay },
        },
      }
    } else {
      // Child has NO animation — inject parent's enter animation with stagger delay
      const parentEnter = parentAnim.enter!
      return {
        ...child,
        animation: {
          enter: { ...parentEnter, delay: (parentEnter.delay ?? 0) + staggerDelay },
          ...(parentAnim.scroll ? { scroll: parentAnim.scroll } : {}),
        },
      }
    }
  })
}

function numericPaddingToToken(px: number): string {
  if (px >= 64) return "16"
  if (px >= 56) return "14"
  if (px >= 48) return "12"
  if (px >= 40) return "10"
  if (px >= 32) return "8"
  if (px >= 24) return "6"
  if (px >= 20) return "5"
  if (px >= 16) return "4"
  if (px >= 12) return "3"
  if (px >= 8) return "2"
  if (px >= 4) return "1"
  return "0.5"
}

/** Convert numeric gap (px) to Tailwind gap token */
function numericGapToToken(px: number): string {
  if (px >= 64) return "16"
  if (px >= 48) return "12"
  if (px >= 32) return "8"
  if (px >= 24) return "6"
  if (px >= 20) return "5"
  if (px >= 16) return "4"
  if (px >= 12) return "3"
  if (px >= 10) return "2.5"
  if (px >= 8) return "2"
  if (px >= 6) return "1.5"
  if (px >= 4) return "1"
  if (px >= 2) return "0.5"
  return "0"
}
